import { BlockRoot, tx } from '@akademi/site-builder-runtime';
import { TradeStyles } from '../styles/TradeStyles';
import { HOME_CSS } from '../styles/homeCss';

type Localized = string | Record<string, string>;

/** One register entry: the claim, and the one line of support copy under it. */
export interface SolarAssuranceClaim {
  title: Localized;
  text?: Localized;
}

export interface SolarAssurancesProps {
  kicker?: Localized;
  title?: Localized;
  lede?: Localized;
  /**
   * The register's entries, in order; a plain string renders as a claim with no
   * support line, so pages seeded before the register keep rendering.
   */
  items?: (Localized | SolarAssuranceClaim)[];
}

/**
 * The assurance register: Horizon's numbered on-dark trust band — `01` to `05`, each claim
 * with one line of support copy, ruled into a single row on wide screens. It replaced the
 * quiet checkmark line of the 2026-09-27 design (`solar/docs/design/redesign-2026-10-01/`,
 * `index.html` `.assure`/`.register`): structural trust, numbered like a register, instead
 * of slogans.
 *
 * <p><b>Only verified claims belong here</b> — the five recorded in the redesign's data pack
 * (`data/catalogue.json` `assurances[]`), each with a live basis: inc/ex VAT price display,
 * datasheets and spec sheets on product pages, trade accounts with band pricing, payment by
 * card or bank transfer, and our own part numbers (AEP-…) on every line. Support copy must
 * stay inside each claim's recorded basis. A claim with no process behind it yet (a delivery
 * window, a stock promise, a phone line's hours) is left out, not softened.
 *
 * <p>An empty register renders nothing at all — a heading over no claims would itself be a
 * claim about the shop.
 */
export function SolarAssurances({ kicker, title, lede, items = [] }: SolarAssurancesProps) {
  const shown = items
    .map((item) => toClaim(item))
    .filter((claim): claim is SolarAssuranceClaim => claim !== null);
  if (!shown.length) return null;

  return (
    <BlockRoot as="section" className="ae ae-register-sec">
      <TradeStyles id="home" css={HOME_CSS} />
      <div className="ae-wrap">
        {tx(kicker) ? <p className="ae-eyebrow">{tx(kicker)}</p> : null}
        {tx(title) ? <h2 className="ae-register__title">{tx(title)}</h2> : null}
        {tx(lede) ? <p className="ae-register__lede">{tx(lede)}</p> : null}
        <ol className="ae-register">
          {shown.map((claim, index) => (
            <li key={index}>
              <span className="ae-register__num" aria-hidden="true">
                {String(index + 1).padStart(2, '0')}
              </span>
              <h3>{tx(claim.title)}</h3>
              {tx(claim.text) ? <p>{tx(claim.text)}</p> : null}
            </li>
          ))}
        </ol>
      </div>
    </BlockRoot>
  );
}

function toClaim(item: Localized | SolarAssuranceClaim): SolarAssuranceClaim | null {
  const claim =
    item && typeof item === 'object' && 'title' in item
      ? (item as SolarAssuranceClaim)
      : { title: item as Localized };
  return tx(claim.title) ? claim : null;
}
